import { useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  Box,
  Button,
  Container,
  Slider,
  Stack,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import HelpDialog from "../components/HelpDialog";

const MIN_ALLOWED_SECONDS = 5;
const DEFAULT_MIN_SECONDS = 10;
const DEFAULT_TOTAL_SECONDS = 180;
const SLIDER_STEP = 5;
const SLIDER_BASE_MAX = 600;

const clampPositiveInteger = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
};

const normalizeTimeRange = (nextMin: number, nextTotal: number): [number, number] => {
  const safeTotal = Math.max(MIN_ALLOWED_SECONDS + 1, clampPositiveInteger(nextTotal));
  const safeMin = Math.min(
    Math.max(MIN_ALLOWED_SECONDS, clampPositiveInteger(nextMin)),
    safeTotal - 1
  );

  return [safeMin, safeTotal];
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (mins === 0) return `${secs}초`;
  if (secs === 0) return `${mins}분`;
  return `${mins}분 ${secs}초`;
};

export default function AlarmGamePage() {
  const navigate = useNavigate();

  const [totalSeconds, setTotalSeconds] = useState(DEFAULT_TOTAL_SECONDS);
  const [elapsed, setElapsed] = useState(0);
  const [status, setStatus] = useState("대기중");
  const [volume, setVolume] = useState(0.5);
  const [minSeconds, setMinSeconds] = useState(DEFAULT_MIN_SECONDS);
  const [openHelp, setOpenHelp] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [isAlarming, setIsAlarming] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);

  const timerRef = useRef<number | null>(null);
  const elapsedRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const startedAtRef = useRef<number | null>(null);

  const controlsDisabled = isRunning || isAlarming;
  const sliderValue: [number, number] = [minSeconds, totalSeconds];
  const sliderMax = Math.max(
    SLIDER_BASE_MAX,
    Math.ceil(totalSeconds / 30) * 30
  );
  const totalMinutes = Math.floor(totalSeconds / 60);

  useEffect(() => {
    const audio = new Audio("/alarm.mp3");
    audio.volume = 0.5;
    audioRef.current = audio;

    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (!isRunning) return;

    if (elapsedRef.current) clearInterval(elapsedRef.current);

    elapsedRef.current = window.setInterval(() => {
      if (!startedAtRef.current) return;
      const nextElapsed = Math.floor(
        (performance.now() - startedAtRef.current) / 1000
      );
      setElapsed(nextElapsed);
    }, 1000);

    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [isRunning]);

  const updateTimeRange = (nextMin: number, nextTotal: number) => {
    const [safeMin, safeTotal] = normalizeTimeRange(nextMin, nextTotal);
    setMinSeconds(safeMin);
    setTotalSeconds(safeTotal);
  };

  const handleRangeChange = (_event: Event, value: number | number[]) => {
    if (!Array.isArray(value)) return;
    updateTimeRange(value[0], value[1]);
  };

  const handleMinutesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextMinutes = clampPositiveInteger(Number(event.target.value));
    updateTimeRange(minSeconds, nextMinutes * 60);
  };

  const handleTotalSecondsChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateTimeRange(minSeconds, Number(event.target.value));
  };

  const handleMinSecondsChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateTimeRange(Number(event.target.value), totalSeconds);
  };

  const resetTimeRange = () => {
    updateTimeRange(DEFAULT_MIN_SECONDS, DEFAULT_TOTAL_SECONDS);
  };

  const prepareAudioPermission = () => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    audio.volume = 0;
    audio.play().then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = volume;
    });
  };

  const playPreviewSound = () => {
    if (!audioRef.current) return;
    const audio = audioRef.current;

    if (isAlarming || isPreviewPlaying) {
      audio.pause();
      audio.currentTime = 0;
      setIsAlarming(false);
      setIsPreviewPlaying(false);
      setStatus("대기중");
      return;
    }

    audio.pause();
    audio.currentTime = 0;
    audio.volume = volume;
    audio.play();
    setIsPreviewPlaying(true);

    audio.onended = () => {
      setIsPreviewPlaying(false);
      if (!isAlarming) setStatus("대기중");
    };
  };

  const playSound = () => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    audio.currentTime = 0;
    audio.play();
    setIsPreviewPlaying(false);
  };

  const stopWaiting = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    startedAtRef.current = null;
    setIsRunning(false);
    setStatus("대기중");
  };

  const stopAlarm = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    startedAtRef.current = null;
    setIsAlarming(false);
    setIsPreviewPlaying(false);
    setStatus("대기중");
  };

  const stopAll = () => {
    stopWaiting();
    stopAlarm();
  };

  const startGame = () => {
    if (totalSeconds <= minSeconds) {
      alert(`${minSeconds}초보다 큰 시간을 설정하세요.`);
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    if (elapsedRef.current) clearInterval(elapsedRef.current);

    stopAll();
    prepareAudioPermission();

    setElapsed(0);
    setStatus("폭탄 작동중...");
    startedAtRef.current = performance.now();
    setIsRunning(true);

    const min = minSeconds;
    const max = totalSeconds;
    const randomTime = Math.floor(Math.random() * (max - min + 1)) + min;

    timerRef.current = window.setTimeout(() => {
      setElapsed(randomTime);
      setStatus("💥 폭탄 폭발!");
      setIsRunning(false);
      setIsAlarming(true);
      playSound();
    }, randomTime * 1000);
  };

  const progress = Math.min((elapsed / totalSeconds) * 100, 100);

  return (
    <Container maxWidth="sm">
      <Paper elevation={4} sx={{ p: 3, mt: 4, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="center" spacing={1}>
            <Typography variant="h4">💣 Random Bomb</Typography>
            <IconButton onClick={() => setOpenHelp(true)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Stack>

          <Box
            sx={{
              fontSize: 64,
              textAlign: "center",
              animation: isRunning ? "pulse 1s ease-in-out infinite" : "none",
              "@keyframes pulse": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.15)" },
                "100%": { transform: "scale(1)" },
              },
            }}
          >
            💣
          </Box>

          <Typography textAlign="center">{status}</Typography>

          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>
                진행시간: {elapsed} / {totalSeconds} 초
              </Typography>

              <Tooltip
                title="현재 시간과 상관없이 설정된 범위 안에서 랜덤하게 알람이 울립니다."
                enterTouchDelay={0}
              >
                <IconButton size="small">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 5, mt: 1 }}
            />
          </Box>

          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography fontWeight={600}>알람 범위</Typography>
                <Tooltip title="기본값으로 초기화" enterTouchDelay={0}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={controlsDisabled}
                      onClick={resetTimeRange}
                      aria-label="알람 범위 기본값으로 초기화"
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {formatDuration(minSeconds)} ~ {formatDuration(totalSeconds)}
              </Typography>
            </Stack>

            <Slider
              disabled={controlsDisabled}
              value={sliderValue}
              min={MIN_ALLOWED_SECONDS}
              max={sliderMax}
              step={SLIDER_STEP}
              disableSwap
              onChange={handleRangeChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => formatDuration(value)}
              getAriaLabel={() => "알람 시간 범위"}
              getAriaValueText={formatDuration}
              sx={{ mt: 2 }}
            />

            <Typography variant="body2" color="text.secondary">
              왼쪽은 최소 알람 시간, 오른쪽은 총 진행 시간을 뜻합니다.
            </Typography>
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>볼륨</Typography>

              <Tooltip
                title={
                  isAlarming || isPreviewPlaying
                    ? "누르면 소리를 즉시 정지합니다."
                    : "누르면 알람음을 미리 재생합니다."
                }
                enterTouchDelay={0}
              >
                <IconButton onClick={playPreviewSound}>
                  <VolumeUpIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            <Slider
              value={volume}
              min={0}
              max={1}
              step={0.01}
              onChange={(_, value) => setVolume(value as number)}
            />
          </Box>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>고급 설정</Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  아래 값은 기본 Range Slider와 실시간으로 동기화됩니다.
                </Typography>

                <TextField
                  fullWidth
                  label="분"
                  type="number"
                  disabled={controlsDisabled}
                  value={totalMinutes}
                  onChange={handleMinutesChange}
                  helperText="분 단위로 총 진행 시간을 빠르게 조절합니다."
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                />

                <TextField
                  fullWidth
                  label="총 초"
                  type="number"
                  disabled={controlsDisabled}
                  value={totalSeconds}
                  onChange={handleTotalSecondsChange}
                  helperText="정밀한 총 진행 시간 설정용입니다."
                  inputProps={{
                    min: MIN_ALLOWED_SECONDS + 1,
                    step: 1,
                  }}
                />

                <TextField
                  fullWidth
                  label="최소 알람 시간(초)"
                  type="number"
                  disabled={controlsDisabled}
                  value={minSeconds}
                  onChange={handleMinSecondsChange}
                  helperText="최소시간이 지나야 알람이 울립니다."
                  inputProps={{
                    min: MIN_ALLOWED_SECONDS,
                    max: Math.max(totalSeconds - 1, MIN_ALLOWED_SECONDS),
                    step: 1,
                  }}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack direction="row" spacing={2}>
            {!isRunning && !isAlarming && (
              <Button fullWidth variant="contained" onClick={startGame}>
                시작
              </Button>
            )}

            {isRunning && (
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                onClick={stopWaiting}
              >
                취소
              </Button>
            )}

            {isAlarming && (
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={stopAlarm}
              >
                알람중지
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Fab
        color="primary"
        sx={{ position: "fixed", bottom: 24, right: 24 }}
        onClick={() => navigate("/list")}
      >
        <MenuIcon />
      </Fab>

      <HelpDialog open={openHelp} onClose={() => setOpenHelp(false)} />
    </Container>
  );
}
